import { useState, useEffect, useCallback, useMemo, useRef } from "react";

export default function RefEntityAutocomplete({ refId, refType, entities, loading, onChange, required = false }) {
  const toLabel = useCallback((id, type) => {
    if (!id) return "";
    const e = entities.find(e => e.id === id && e.ref_type === type);
    return e ? e.label : id.slice(0, 8) + "…";
  }, [entities]);

  const [query,     setQuery]     = useState(() => toLabel(refId, refType));
  const [open,      setOpen]      = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);
  const wrapRef = useRef(null);
  const listRef = useRef(null);

  useEffect(() => { setQuery(toLabel(refId, refType)); }, [refId, refType, toLabel]);
  useEffect(() => { setActiveIdx(-1); }, [query]);
  useEffect(() => {
    if (activeIdx >= 0 && listRef.current) {
      listRef.current.children[activeIdx]?.scrollIntoView({ block: "nearest" });
    }
  }, [activeIdx]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const base = q
      ? entities.filter(e =>
          e.label.toLowerCase().includes(q) ||
          e.sublabel?.toLowerCase().includes(q) ||
          e.sub_feature?.toLowerCase().includes(q) ||
          e.description?.toLowerCase().includes(q) ||
          e.ref_type.toLowerCase().includes(q)
        )
      : entities;
    return base.slice(0, 60);
  }, [query, entities]);

  const select = (item) => {
    setQuery(item.label);
    onChange(item.id, item.ref_type);
    setOpen(false);
  };

  const clear = () => {
    setQuery("");
    onChange("", "");
    setOpen(false);
  };

  const handleKeyDown = (e) => {
    if      (e.key === "ArrowDown") { e.preventDefault(); setActiveIdx(p => Math.min(p + 1, filtered.length - 1)); }
    else if (e.key === "ArrowUp")   { e.preventDefault(); setActiveIdx(p => Math.max(p - 1, 0)); }
    else if (e.key === "Enter") {
      e.preventDefault();
      if (activeIdx >= 0 && filtered[activeIdx]) select(filtered[activeIdx]);
      else if (filtered.length === 1) select(filtered[0]);
    }
    else if (e.key === "Escape") { setOpen(false); }
  };

  const isEmpty = !refId;

  return (
    <div className="dts-ac-wrap" ref={wrapRef}>
      <div className={`dts-ac-input-row${required && isEmpty ? " dts-ac-input-row--required" : ""}`}>
        <input
          className="dts-ac-input"
          value={query}
          placeholder={loading ? "Loading entities…" : "Search task / defect…"}
          disabled={loading}
          onFocus={() => setOpen(true)}
          onChange={e => { setQuery(e.target.value); setOpen(true); if (!e.target.value) onChange("", ""); }}
          onKeyDown={handleKeyDown}
          onBlur={e => { if (wrapRef.current?.contains(e.relatedTarget)) return; setOpen(false); }}
        />
        {refId && (
          <button className="dts-ac-clear" onMouseDown={e => { e.preventDefault(); clear(); }} title="Clear">✕</button>
        )}
      </div>

      {refId && (
        <div className="dts-ac-pill">
          <span className="dts-ac-pill-type">{refType}</span>
          <span className="dts-ac-pill-id">{refId}</span>
        </div>
      )}

      {open && !loading && (
        <div className="dts-ac-dropdown" ref={listRef}>
          {filtered.length === 0 && (
            <div className="dts-ac-empty">No results for "{query}"</div>
          )}
          {filtered.map((item, i) => (
            <div
              key={`${item.ref_type}-${item.id}`}
              className={`dts-ac-option${i === activeIdx ? " dts-ac-option--active" : ""}`}
              tabIndex={-1}
              onMouseDown={e => { e.preventDefault(); select(item); }}
            >
              <span className="dts-ac-opt-type">{item.ref_type}</span>
              <div className="dts-ac-opt-text">
                <span className="dts-ac-opt-label">{item.label}</span>
                {item.sublabel && <span className="dts-ac-opt-sub">{item.sublabel}</span>}
                {item.sub_feature && <span className="dts-ac-opt-sub">↳ {item.sub_feature}</span>}
                {item.description && <span className="dts-ac-opt-desc">{item.description}</span>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
